"use client"

import { Card } from "@/components/ui/card"
import { StoreProduct, StoreProductReview } from "@/types/store"
import { motion } from "framer-motion"
import { Clock, Sparkles, Star, User, Globe, Monitor, Shield, Baby, Users, UserCheck, AlertTriangle } from "lucide-react"
import Image from "next/image"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import ProductReviewForm from "../product-review-form"

type ProductTabsProps = {
  product: StoreProduct
  reviews: StoreProductReview
  canReview: boolean | null
}

// Helper function to get rating system info
// Image S3 Uploads to be done 
const getRatingSystemInfo = (ratingSystem: string) => {
  const systems = {
    usk: {
      name: "USK Rating",
      icon: Shield,
      color: "text-blue-400",
      bgColor: "bg-blue-900",
      ratings: {
        "0": { label: "USK 0", icon: "/images_public/usk0.webp", color: "text-green-400" },
        "6": { label: "USK 6", icon: "/images_public/usk6.png", color: "text-yellow-400" },
        "12": { label: "USK 12", icon: "/images_public/usk12.svg", color: "text-orange-400" },
        "16": { label: "USK 16", icon: "/images_public/16usk.png", color: "text-red-400" },
        "18": { label: "USK 18", icon: "/images_public/usk18.png", color: "text-red-500" }
      }
    },
    esrb: {
      name: "ESRB Rating",
      icon: Shield,
      color: "text-purple-400",
      bgColor: "bg-purple-900",
      ratings: {
        "everyone": { label: "ESRB Everyone", icon: "/images_public/e.svg", color: "text-green-400" },
        "everyone10": { label: "ESRB Everyone 10+", icon: "/images_public/everyone10.png", color: "text-yellow-400" },
        "teen": { label: "ESRB Teen", icon: "/images_public/esrbteen.png", color: "text-orange-400" },
        "mature": { label: "ESRB Mature", icon: "/images_public/esrbmature.png", color: "text-red-400" },
        "adults": { label: "ESRB Adults Only", icon: "/images_public/esrbadults.png", color: "text-red-500" }
      }
    },
    pegi: {
      name: "PEGI Rating",
      icon: Shield,
      color: "text-green-400",
      bgColor: "bg-green-900",
      ratings: {
        "3": { label: "PEGI 3", icon: "/images_public/pegi3.png", color: "text-green-400" },
        "7": { label: "PEGI 7", icon: "/images_public/pegi7.svg", color: "text-yellow-400" },
        "12": { label: "PEGI 12", icon: "/images_public/pegi12.png", color: "text-orange-400" },
        "16": { label: "PEGI 16", icon: "/images_public/16pegi.jpg", color: "text-red-400" },
        "18": { label: "PEGI 18", icon: "/images_public/pegi18.png", color: "text-red-500" },
        "ingamepurchases": { label: "In-Game Purchases", icon: "/images_public/ingamepurchases.png", color: "text-orange-400" }
      }
    }
  }
  
  return systems[ratingSystem as keyof typeof systems] || null
}

const ProductTabs = ({ product, reviews, canReview }: ProductTabsProps) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState("description")

  const platforms = product.platforms ? JSON.parse(product.platforms) : []
  const ageList = product.age_list ? JSON.parse(product.age_list) : {}
  const regionList = product.region_list ? JSON.parse(product.region_list) : {}

  const tabs = [
    { id: "description", label: t("product.productDescription"), icon: "📝" },
    { id: "reviews", label: t("product.reviewsCount", { count: reviews?.data?.length || 0 }), icon: "⭐" },
    { id: "refund", label: t("product.refundPolicy"), icon: "💰" },
  ]

  const parseJsonSafe = (val: unknown) => {
    if (typeof val === "string") {
      try {
        return JSON.parse(val)
      } catch {
        return null
      }
    }
    return val
  }

  const categoriesData: any[] = (() => {
    if (!product.categories) return []
    if (typeof product.categories === "string") {
      const parsed = parseJsonSafe(product.categories)
      return Array.isArray(parsed) ? parsed : []
    }
    return Array.isArray(product.categories) ? (product.categories as any[]) : []
  })()

  const primaryCat: any | null = categoriesData.length ? categoriesData[0] : null

  const maskNamePart = (part?: string) => {
    if (!part) return "";
    const trimmed = String(part).trim();
    const visible = trimmed.slice(0, 3);
    return `${visible}${trimmed.length > 3 ? "***" : ""}`;
  };

  const formatDateLLL = (value?: string) => {
    if (!value) return "";
    try {
      return new Intl.DateTimeFormat("tr-TR", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value));
    } catch {
      return String(value);
    }
  };

  const content = {
    description: (
      <div className="space-y-4 text-slate-700">
        {product?.description && (
          <div
            className="text-base leading-relaxed [&>p]:mb-1 [&>p:last-child]:mb-0 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-1 [&>ul:last-child]:mb-0 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-1 [&>ol:last-child]:mb-0 [&>li]:mb-1 [&>li:last-child]:mb-0 [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mb-1 [&>h2]:text-xl [&>h2]:font-semibold [&>h2]:mb-2 [&>h3]:text-lg [&>h3]:font-medium [&>h3]:mb-2 [&>strong]:font-semibold [&>em]:italic [&>code]:bg-slate-100 [&>code]:px-1 [&>code]:py-0.5 [&>code]:rounded [&>code]:text-sm [&>blockquote]:border-l-4 [&>blockquote]:border-slate-300 [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-slate-700"
            dangerouslySetInnerHTML={{ __html: product.description }}
          />
        )}
      </div>
    ),
    platforms: (
      <div className="space-y-4 text-slate-700">
        <div className="flex items-center gap-2 mb-4">
          <Monitor className="text-purple-400" size={20} />
          <h3 className="text-lg font-medium">{t("product.supportedPlatforms")}</h3>
        </div>
        {platforms ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {platforms.map((platform: any, index: number) => (
              <Card key={index} className="p-4 bg-bg-component">
                <div className="flex items-center gap-3">
                  {platform.icon_url ? (
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-700 flex items-center justify-center">
                      <Image
                        src={`${process.env.NEXT_PUBLIC_S3_IMAGE_BASE_URL}${platform.icon_url}`}
                        alt={platform.name}
                        width={48}
                        height={48}
                        className="object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center">
                      <Monitor className="text-slate-600" size={24} />
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <p className="text-slate-600">{t("product.noPlatformInfo")}</p>
        )}
      </div>
    ),
    regions: (
      <div className="space-y-4 text-slate-700">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="text-purple-400" size={20} />
          <h3 className="text-lg font-medium">{t("product.availableRegions")}</h3>
        </div>
        {regionList ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(regionList).map(([code, name]: [string, any], index: number) => (
              <Card key={index} className="p-4 bg-bg-component">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center">
                    <Globe className="text-slate-600" size={24} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-slate-900">{name}</h4>
                    <p className="text-sm text-slate-600 mt-1">Code: {code}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <p className="text-slate-600">{t("product.noRegionInfo")}</p>
        )}
      </div>
    ),
    age: (
      <div className="space-y-4 text-slate-700">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="text-purple-400" size={20} />
          <h3 className="text-lg font-medium">{t("product.ageRestrictions")}</h3>
        </div>
        {ageList ? (
          <div className="space-y-4">
            {Object.entries(ageList).map(([rating, ages]: [string, any], index: number) => {
              const ratingSystemInfo = getRatingSystemInfo(rating);
              if (!ratingSystemInfo) {
                return null;
              }

              return (
                <Card key={index} className="p-4 bg-bg-component">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-lg ${ratingSystemInfo.bgColor} flex items-center justify-center`}>
                      <ratingSystemInfo.icon className={ratingSystemInfo.color} size={24} />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">{ratingSystemInfo.name}</h4>
                      <p className="text-sm text-slate-600">{t("product.ageClassificationSystem")}</p>
                    </div>
                  </div>
                  <div className="ml-15">
                    {Array.isArray(ages) && ages.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {ages.map((age: string, ageIndex: number) => {
                          const ratingInfo = (ratingSystemInfo.ratings as any)[age];
                          if (!ratingInfo) {
                            return null;
                          }
                          
                          const isImagePath = typeof ratingInfo.icon === 'string';
                          
                          return (
                            <div key={ageIndex} className={`p-3 rounded-lg border border-gray-700 bg-gray-800/50 flex items-center gap-3`}>
                              <div className={`w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center bg-gray-700`}>
                                {isImagePath ? (
                                  <Image
                                    src={ratingInfo.icon}
                                    alt={ratingInfo.label}
                                    width={48}
                                    height={48}
                                    className="object-cover"
                                  />
                                ) : (
                                  <div className={`w-12 h-12 rounded-full ${ratingInfo.color.replace('text-', 'bg-')} bg-opacity-20 flex items-center justify-center`}>
                                    <ratingInfo.icon className={ratingInfo.color} size={24} />
                                  </div>
                                )}
                              </div>
                              <span className="text-sm font-medium text-slate-900">
                                {ratingInfo.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-slate-600">{t("product.noSpecificAgeRestrictions")}</p>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <p className="text-slate-600">{t("product.noAgeRestrictionInfo")}</p>
        )}
      </div>
    ),
    reviews: (
      <div className="content-container flex flex-col gap-4 pb-5">
        <div className="flex w-full justify-between rounded-xl border border-orange-100 bg-orange-50/70 p-4">
          <div className="flex gap-4 ">
            <Sparkles className="text-button-primary-lighten" />
            <h5>{product?.title} {t("product.reviews")}</h5>
          </div>
          <h6 className="text-sm text-ui-fg-subtle">{reviews?.data?.length || 0} {t("product.reviews")}</h6>
        </div>
        <div className="flex flex-col gap-3">
          {(reviews?.data || []).map((review: StoreProductReview["data"][0], i: number) => {
            const formattedDate = formatDateLLL(review.created_at)
            const initials = `${(review.customer_name || "").charAt(0)}${(review.customer_last_name || "").charAt(0)}`.toUpperCase()
            const maskedFullName = `${maskNamePart(review.customer_name)} ${maskNamePart(review.customer_last_name)}`.trim()
            return (
              <Card key={i} className="border-slate-200 bg-white p-4">
                <div className="flex gap-4">
                  <div className="relative shrink-0">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 text-sm font-medium text-orange-700">
                      {initials || "?"}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                      <div className="flex items-center gap-2 text-sm text-ui-fg-subtle">
                        <User size={16} />
                        <span className="truncate">{maskedFullName || t("product.anonymous")}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-ui-fg-muted">
                        <Clock size={14} />
                        <span>{formattedDate}</span>
                      </div>
                    </div>

                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <span className="font-medium">{review.rating}</span>
                      <div className="inline-flex gap-0.5">
                        {Array.from({ length: 5 }, (_, idx) => (
                          <Star
                            key={idx}
                            size={14}
                            color="#ffc107"
                            fill={idx < (review.rating || 0) ? "#ffc107" : "none"}
                          />
                        ))}
                      </div>
                    </div>

                    <p className="mt-3 text-sm text-ui-fg-muted break-words whitespace-pre-wrap">
                      {review.comment}
                    </p>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
        <div className="w-full flex flex-col items-start justify-between">
          {canReview ? (
            <ProductReviewForm productId={product.id} />
          ) : (
            <div className="text-sm text-ui-fg-subtle">
              {t("product.mustPurchaseToReview")}
            </div>
          )}
        </div>
      </div>
    ),
    requirements: (
      <div className="space-y-4 text-slate-700">
        {(() => {
          const html = primaryCat?.system_requirements
          return html ? (
            <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
          ) : (
            <p className="text-slate-600">{t("product.noSystemRequirements")}</p>
          )
        })()}
      </div>
    ),
    howto: (
      <div className="space-y-4 text-slate-700">
        {(() => {
          const html = primaryCat?.how_to_use
          return html ? (
            <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
          ) : (
            <p className="text-slate-600">{t("product.noHowToInfo")}</p>
          )
        })()}
      </div>
    ),
    refund: (
      <div className="space-y-4 text-slate-700">
        {(() => {
          const html = primaryCat?.refund_info
          return html ? (
            <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
          ) : (
            <p className="text-slate-600">{t("product.noRefundPolicyInfo")}</p>
          )
        })()}
      </div>
    ),
    seo: (
      <div className="space-y-4 text-slate-700">
        {(() => {
          const seoRaw = primaryCat?.seo
          const seo = typeof seoRaw === "string" ? parseJsonSafe(seoRaw) : seoRaw
          if (!seo) return <p className="text-slate-600">{t("product.noSeoData")}</p>
          return (
            <div className="space-y-3">
              {seo.meta_title && (
                <div>
                  <h4 className="text-slate-900 font-medium mb-1">{t("product.metaTitle")}</h4>
                  <p className="text-slate-700">{seo.meta_title}</p>
                </div>
              )}
              {seo.meta_description && (
                <div>
                  <h4 className="text-slate-900 font-medium mb-1">{t("product.metaDescription")}</h4>
                  <p className="text-slate-700">{seo.meta_description}</p>
                </div>
              )}
              {seo.meta_key && (
                <div>
                  <h4 className="text-slate-900 font-medium mb-1">{t("product.metaKeywords")}</h4>
                  <p className="text-slate-700">
                    {Array.isArray(seo.meta_key) ? seo.meta_key.join(", ") : seo.meta_key}
                  </p>
                </div>
              )}
              {seo.seo_content && (
                <div>
                  <h4 className="text-slate-900 font-medium mb-1">{t("product.seoContent")}</h4>
                  <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: seo.seo_content }} />
                </div>
              )}
            </div>
          )
        })()}
      </div>
    ),
  }

  return (
    <div className="content-container px-4 small:px-6">
      <div className="mt-6 flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-gradient-to-r from-orange-50/50 via-white to-orange-50/50">
          <div className="flex overflow-x-auto no-scrollbar">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 text-sm flex items-center gap-2.5 whitespace-nowrap transition-all duration-200 relative min-w-fit
                ${activeTab === tab.id
                    ? "font-semibold text-orange-700"
                    : "text-slate-600 hover:text-slate-900"
                  }`}
              >
                <span className="text-base">{tab.icon}</span>
                {tab.label}
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-1 p-6">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {content[activeTab as keyof typeof content] || (
              <div>
                Content for {activeTab} tab is coming soon...
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default ProductTabs
